import type {
  Plant,
  PlantMorphology,
  PlantPhenology,
  PlantSeedStarting,
  PlantLifecycle,
  PlantSource,
  PlantNativeRange,
  PlantTaxonomy,
  PlantTemperatureRequirements,
  PlantTrait,
  PlantVariety,
  TraditionalMedicinalUse,
} from '@/data/plants';
import type { Pest, PestSolution, PestType } from '@/data/pests';
import {
  formatGenerationFailureMessage,
  GeneratedProfileRequestError,
  type GenerationErrorResponse,
} from '@/lib/generationDiagnostics';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface GeneratedPlant {
  id: string;
  name: string;
  scientificName: string;
  status?: 'draft' | 'approved';
  type: string;
  traits?: PlantTrait[];
  description: string;
  sunlight: string;
  water: string;
  soilType?: string;
  soil: string;
  spacing: string;
  daysToMaturity: Plant['daysToMaturity'] | number;
  zones: number[];
  companions: string[];
  antagonists?: string[];
  avoid: string[];
  planting?: Plant['planting'];
  phenology?: PlantPhenology;
  indoorStart: string | null;
  transplant: string | null;
  directSow: string | null;
  harvest: string;
  harvestWindow?: {
    months: number[];
    zoneShift?: {
      cold?: number;
      mid?: number;
      warm?: number;
    };
  };
  seedStarting?: PlantSeedStarting;
  lifecycle?: PlantLifecycle;
  organicTips: string[];
  pests?: string[];
  commonPests: string[];
  tempRequirements?: PlantTemperatureRequirements;
  taxonomy?: PlantTaxonomy;
  naturalHistory?: string;
  traditionalMedicinalUse?: TraditionalMedicinalUse;
  morphology?: PlantMorphology;
  varieties?: PlantVariety[];
  nativeRange?: PlantNativeRange;
  image?: string | null;
  source?: PlantSource;
  isGenerated: true;
  generatedAt: string;
}

export interface GeneratedPest {
  id: string;
  name: string;
  scientificName?: string | null;
  status?: 'draft' | 'approved';
  type: PestType;
  tags?: string[];
  description: string;
  symptoms: string[];
  affectedPlants: string[];
  causes?: string[];
  lifecycle?: string;
  spread?: string;
  organicSolutions: PestSolution[];
  preventionTips: string[];
  lookalikes?: Pest['lookalikes'];
  image?: string | null;
  source?: Pest['source'];
  isGenerated: true;
  generatedAt: string;
}

async function readGenerationError(response: Response): Promise<GeneratedProfileRequestError> {
  try {
    const payload = await response.json() as Partial<GenerationErrorResponse> & { error?: string };
    if (payload.success === false || payload.stage || payload.message) {
      return new GeneratedProfileRequestError(
        payload.message ?? payload.error ?? 'Generation failed.',
        {
          stage: payload.stage,
          details: payload.details,
          statusCode: response.status,
        },
      );
    }

    return new GeneratedProfileRequestError(payload.error ?? 'Generation failed.', { statusCode: response.status });
  } catch {
    return new GeneratedProfileRequestError(`Generation request failed with status ${response.status}.`, {
      statusCode: response.status,
    });
  }
}

export function formatGeneratedProfileError(error: unknown, fallback = 'Generation failed.'): string {
  if (error instanceof GeneratedProfileRequestError) {
    return formatGenerationFailureMessage(error);
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
}

// ── Plant helpers ─────────────────────────────────────────────────────────────

/** Fetch a single generated plant from KV (via API). Returns null if not found. */
export async function getGeneratedPlant(name: string): Promise<GeneratedPlant | null> {
  const slug = name.toLowerCase().replace(/\s+/g, '-');
  try {
    const res = await fetch(`/api/generate-profile?type=plant&id=generated-${slug}`);
    if (!res.ok) return null;
    return await res.json() as GeneratedPlant;
  } catch {
    return null;
  }
}

/** Fetch a single generated plant from KV by its stored id. Returns null if not found. */
export async function getGeneratedPlantById(id: string): Promise<GeneratedPlant | null> {
  try {
    const res = await fetch(`/api/generate-profile?type=plant&id=${encodeURIComponent(id)}`);
    if (!res.ok) return null;
    return await res.json() as GeneratedPlant;
  } catch {
    return null;
  }
}

/** Call the generation API to generate a plant profile and cache it in KV. */
export async function generatePlantProfile(name: string): Promise<GeneratedPlant> {
  try {
    const res = await fetch('/api/generate-profile', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ type: 'plant', name }),
    });
    if (!res.ok) throw await readGenerationError(res);
    const data = await res.json() as GeneratedPlant;
    return data;
  } catch (error) {
    if (error instanceof GeneratedProfileRequestError) throw error;
    throw new GeneratedProfileRequestError('Generation API request failed.', {
      details: { classification: 'api_failure' },
    });
  }
}

/** Search previously generated plant profiles whose names contain the query. */
export async function searchGeneratedPlants(query: string): Promise<GeneratedPlant[]> {
  try {
    const params = new URLSearchParams({ type: 'plant', q: query });
    const res    = await fetch(`/api/generate-profile/search?${params}`);
    if (!res.ok) return [];
    const data = await res.json() as { profiles: GeneratedPlant[] };
    return data.profiles ?? [];
  } catch {
    return [];
  }
}

// ── Pest helpers ──────────────────────────────────────────────────────────────

/** Fetch a single generated pest from KV (via API). Returns null if not found. */
export async function getGeneratedPest(name: string): Promise<GeneratedPest | null> {
  const slug = name.toLowerCase().replace(/\s+/g, '-');
  try {
    const res = await fetch(`/api/generate-profile?type=pest&id=generated-${slug}`);
    if (!res.ok) return null;
    return await res.json() as GeneratedPest;
  } catch {
    return null;
  }
}

/** Call the generation API to generate a pest profile and cache it in KV. */
export async function generatePestProfile(name: string): Promise<GeneratedPest> {
  try {
    const res = await fetch('/api/generate-profile', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ type: 'pest', name }),
    });
    if (!res.ok) throw await readGenerationError(res);
    const data = await res.json() as GeneratedPest;
    return data;
  } catch (error) {
    if (error instanceof GeneratedProfileRequestError) throw error;
    throw new GeneratedProfileRequestError('Generation API request failed.', {
      details: { classification: 'api_failure' },
    });
  }
}

/** Search previously generated pest profiles whose names contain the query. */
export async function searchGeneratedPests(query: string): Promise<GeneratedPest[]> {
  try {
    const params = new URLSearchParams({ type: 'pest', q: query });
    const res    = await fetch(`/api/generate-profile/search?${params}`);
    if (!res.ok) return [];
    const data = await res.json() as { profiles: GeneratedPest[] };
    return data.profiles ?? [];
  } catch {
    return [];
  }
}
